'use client';

import React, { createContext, useContext, useMemo, useState } from 'react';

import type { BaseSubscriptionPlan } from '@wwe-portal/entities/subscription.type';

import type {
  PlanChangeDialogState,
  SubscriptionContextValue,
  SubscriptionData,
  SubscriptionState,
  UserSubscriptionMutationState,
} from './type';
import { LoginDialogProps } from '@wwe-portal/ui/features/auth/login-dialog';

const SubscriptionContext = createContext<SubscriptionContextValue | null>(
  null
);

export interface SubscriptionProviderProps extends SubscriptionContextValue {
  children: React.ReactNode;
}

export interface SubscriptionContextProps {
  children: React.ReactNode;

  isLoading: boolean;
  data: SubscriptionData | undefined;
  currentPlan: BaseSubscriptionPlan | undefined;

  onLoginWithFacebook?: () => void;
  onLoginWithScopely?: () => void;

  loginState: LoginDialogProps;
  mutation: UserSubscriptionMutationState;
  changeDialogState: PlanChangeDialogState | undefined;
}

const useSubsciptionViewStore = ({
  data,
  isLoading,
}: {
  isLoading: boolean;
  data: SubscriptionData | undefined;
}) => {
  const [showingMorePlanIds, setShowingMorePlanIds] = useState(
    new Set<string>()
  );

  const subscription: SubscriptionState = {
    isLoading,
    data,
    showingMorePlanIds,

    showMoreInfo: (planId: string) => {
      const nextSet = new Set(showingMorePlanIds);
      nextSet.add(planId);
      setShowingMorePlanIds(nextSet);
    },
    hideMoreInfo: (planId: string) => {
      const nextSet = new Set(showingMorePlanIds);
      nextSet.delete(planId);
      setShowingMorePlanIds(nextSet);
    },
  };

  return subscription;
};

export function SubscriptionProvider({
  data,
  isLoading,
  currentPlan,
  loginState,
  changeDialogState,
  mutation,

  children,
}: Readonly<SubscriptionContextProps>) {
  const subscription = useSubsciptionViewStore({ data, isLoading });

  const value = useMemo(
    () => ({
      isLoading,
      loginState,
      subscription,
      currentPlan,
      mutation,
      changeDialogState,
    }),
    [
      isLoading,
      loginState,
      subscription,
      currentPlan,
      mutation,
      changeDialogState,
    ]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (context == null) {
    throw new Error('SubscriptionContext is null');
  }
  return context;
};
